import { useEffect, useMemo } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Radio,
  Tabs,
  Button,
  Space,
  Typography,
  DatePicker,
  Tag,
  Spin,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useProductById,
  useCreateProduct,
  useUpdateProduct,
  useUnits,
} from '@hooks/useDictionaries';
import type { CreateProductRequest, ProductCategory } from '@/types/dictionaries';

// Ключі — члени C# enum ProductCategory
const MD_CATEGORY_OPTIONS: { value: ProductCategory; label: string }[] = [
  { value: 'DiagnosticEquipment', label: 'Діагностичне обладнання' },
  { value: 'SurgicalInstruments', label: 'Хірургічні інструменти' },
  { value: 'ImplantableDevices', label: 'Імплантовані вироби' },
  { value: 'Consumables', label: 'Витратні матеріали' },
  { value: 'LaboratoryEquipment', label: 'Лабораторне обладнання' },
  { value: 'ImagingEquipment', label: 'Обладнання для візуалізації' },
  { value: 'MonitoringDevices', label: 'Моніторингові пристрої' },
  { value: 'RehabilitationEquipment', label: 'Реабілітаційне обладнання' },
  { value: 'SterilizationEquipment', label: 'Стерилізаційне обладнання' },
  { value: 'Other', label: 'Інше' },
];

const MD_CURRENCY_OPTIONS = [
  { value: 'UAH', label: 'UAH' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];

interface ProductEditPageProps {
  productId?: string;
  onBack: () => void;
}

export default function ProductEditPage({ productId, onBack }: ProductEditPageProps) {
  const [mdForm] = Form.useForm<CreateProductRequest>();
  const { data: mdProduct, isLoading: mdIsLoadingProduct } = useProductById(productId);
  const { data: mdUnits } = useUnits();
  const mdCreateProduct = useCreateProduct();
  const mdUpdateProduct = useUpdateProduct();

  const mdIsEditing = !!productId;

  useEffect(() => {
    if (mdProduct) {
      mdForm.setFieldsValue({
        ...mdProduct,
        dsmdRegistrationExpiry: mdProduct.dsmdRegistrationExpiry || undefined,
        ceExpiry: mdProduct.ceExpiry || undefined,
      });
    }
  }, [mdProduct, mdForm]);

  const mdHandleSave = async () => {
    const mdValues = await mdForm.validateFields();
    if (mdIsEditing && productId) {
      await mdUpdateProduct.mutateAsync({ ...mdValues, id: productId });
    } else {
      await mdCreateProduct.mutateAsync(mdValues);
    }
    onBack();
  };

  const mdPurchasePrice = Form.useWatch('defaultPurchasePrice', mdForm);
  const mdSalePrice = Form.useWatch('defaultSalePrice', mdForm);
  const mdPurchaseCurrency = Form.useWatch('defaultPurchaseCurrency', mdForm);
  const mdSaleCurrency = Form.useWatch('defaultSaleCurrency', mdForm);

  const mdMarginPercent = useMemo(() => {
    // Маржа має сенс лише в одній валюті
    if (
      mdPurchaseCurrency === mdSaleCurrency &&
      mdPurchasePrice !== undefined &&
      mdPurchasePrice !== null &&
      mdPurchasePrice > 0 &&
      mdSalePrice !== undefined &&
      mdSalePrice !== null &&
      mdSalePrice > 0
    ) {
      return (((mdSalePrice - mdPurchasePrice) / mdPurchasePrice) * 100).toFixed(1);
    }
    return '—';
  }, [mdPurchasePrice, mdSalePrice, mdPurchaseCurrency, mdSaleCurrency]);

  const mdDsmdExpiry = Form.useWatch('dsmdRegistrationExpiry', mdForm);
  const mdCeExpiry = Form.useWatch('ceExpiry', mdForm);

  const mdGetExpiryTag = (mdDateStr: string | null | undefined) => {
    if (!mdDateStr) return null;
    const mdDate = dayjs(mdDateStr);
    const mdNow = dayjs();
    if (mdDate.isBefore(mdNow)) {
      return <Tag color="red">Прострочений</Tag>;
    }
    if (mdDate.diff(mdNow, 'day') < 90) {
      return <Tag color="orange">Спливає скоро</Tag>;
    }
    return <Tag color="green">Дійсний</Tag>;
  };

  if (mdIsEditing && mdIsLoadingProduct) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  // forceRender: поля неактивних вкладок мають бути зареєстровані, інакше validateFields їх не поверне,
  // а UpdateProductCommand перезапише відсутні ціни/регуляторні дані значенням null
  const mdTabItems = [
    {
      key: 'general',
      label: 'Загальна інформація',
      forceRender: true,
      children: (
        <div style={{ maxWidth: 800 }}>
          <Form.Item
            name="sku"
            label="SKU"
            rules={[{ required: true, message: 'Введіть SKU' }]}
          >
            <Input maxLength={50} disabled={mdIsEditing} />
          </Form.Item>
          <Form.Item
            name="name"
            label="Назва"
            rules={[{ required: true, message: 'Введіть назву' }]}
          >
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="description" label="Опис">
            <Input.TextArea rows={3} maxLength={1000} />
          </Form.Item>
          <Form.Item
            name="category"
            label="Категорія"
            rules={[{ required: true, message: 'Оберіть категорію' }]}
          >
            <Select
              placeholder="Оберіть категорію"
              options={MD_CATEGORY_OPTIONS}
            />
          </Form.Item>
          <Form.Item name="unitOfMeasureId" label="Одиниця виміру">
            <Select
              allowClear
              placeholder="Оберіть одиницю"
              options={mdUnits?.map((mdUnit) => ({
                value: mdUnit.id,
                label: `${mdUnit.name} (${mdUnit.shortName})`,
              }))}
            />
          </Form.Item>
          <Form.Item name="manufacturerName" label="Виробник">
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="countryOfOrigin" label="Країна">
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="hsCode" label="Код УКТ ЗЕД">
            <Input maxLength={20} />
          </Form.Item>
          <Form.Item name="internalNotes" label="Внутрішні нотатки">
            <Input.TextArea rows={3} maxLength={2000} />
          </Form.Item>
        </div>
      ),
    },
    {
      key: 'classification',
      label: 'Класифікація та UDI',
      forceRender: true,
      children: (
        <div style={{ maxWidth: 800 }}>
          <Form.Item
            name="riskClass"
            label="Клас ризику"
            rules={[{ required: true, message: 'Оберіть клас ризику' }]}
          >
            <Radio.Group>
              <Radio value="I">I</Radio>
              <Radio value="IIa">IIa</Radio>
              <Radio value="IIb">IIb</Radio>
              <Radio value="III">III</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="requiresSerialNumber"
            label="Потребує серійний номер"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="requiresBatchNumber"
            label="Потребує номер партії"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item name="isImplant" label="Імплант" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="isSterile" label="Стерильний" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="shelfLifeDays"
            label="Термін придатності (днів)"
            rules={[{ required: true, message: 'Введіть термін придатності' }]}
          >
            <InputNumber min={0} max={99999} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="gtin" label="GTIN">
            <Input maxLength={14} />
          </Form.Item>
          <Form.Item name="udiDi" label="UDI DI">
            <Input maxLength={100} />
          </Form.Item>
        </div>
      ),
    },
    {
      key: 'prices',
      label: 'Ціни',
      forceRender: true,
      children: (
        <div style={{ maxWidth: 600 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Form.Item name="defaultPurchasePrice" label="Ціна закупівлі" style={{ flex: 1 }}>
              <InputNumber min={0.01} precision={2} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="defaultPurchaseCurrency" label="Валюта" style={{ width: 110 }}>
              <Select options={MD_CURRENCY_OPTIONS} />
            </Form.Item>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Form.Item name="defaultSalePrice" label="Ціна продажу" style={{ flex: 1 }}>
              <InputNumber min={0.01} precision={2} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="defaultSaleCurrency" label="Валюта" style={{ width: 110 }}>
              <Select options={MD_CURRENCY_OPTIONS} />
            </Form.Item>
          </div>
          <Form.Item label="Маржа (%)">
            <Typography.Text strong style={{ fontSize: 16 }}>
              {mdMarginPercent}%
            </Typography.Text>
          </Form.Item>
        </div>
      ),
    },
    {
      key: 'regulatory',
      label: 'Регуляторні',
      forceRender: true,
      children: (
        <div style={{ maxWidth: 800 }}>
          <Form.Item name="dsmdRegistrationNumber" label="ДСМД номер">
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="dsmdRegistrationExpiry" label="ДСМД дійсний до">
            <Space>
              <DatePicker
                format="YYYY-MM-DD"
                value={mdDsmdExpiry ? dayjs(mdDsmdExpiry) : null}
                onChange={(mdDate) =>
                  mdForm.setFieldValue(
                    'dsmdRegistrationExpiry',
                    mdDate ? mdDate.format('YYYY-MM-DD') : undefined
                  )
                }
              />
              {mdGetExpiryTag(mdDsmdExpiry)}
            </Space>
          </Form.Item>
          <Form.Item name="ceNumber" label="CE номер">
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="ceExpiry" label="CE дійсний до">
            <Space>
              <DatePicker
                format="YYYY-MM-DD"
                value={mdCeExpiry ? dayjs(mdCeExpiry) : null}
                onChange={(mdDate) =>
                  mdForm.setFieldValue(
                    'ceExpiry',
                    mdDate ? mdDate.format('YYYY-MM-DD') : undefined
                  )
                }
              />
              {mdGetExpiryTag(mdCeExpiry)}
            </Space>
          </Form.Item>
          <Form.Item name="ukrSepro" label="УкрСЕПРО">
            <Input maxLength={100} />
          </Form.Item>
        </div>
      ),
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          Назад
        </Button>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {mdIsEditing ? 'Редагувати позицію' : 'Нова позиція'}
        </Typography.Title>
      </Space>

      <Form
        form={mdForm}
        layout="vertical"
        initialValues={{
          requiresSerialNumber: false,
          requiresBatchNumber: true,
          isImplant: false,
          isSterile: false,
          shelfLifeDays: 0,
          riskClass: 'I',
          // Дефолти сервера, коли валюту не передано
          defaultPurchaseCurrency: 'USD',
          defaultSaleCurrency: 'UAH',
        }}
      >
        <Tabs items={mdTabItems} />
      </Form>

      <div
        style={{
          padding: '16px 0',
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          borderTop: '1px solid #f0f0f0',
          marginTop: 16,
        }}
      >
        <Button onClick={onBack}>Скасувати</Button>
        <Button
          type="primary"
          onClick={mdHandleSave}
          loading={mdCreateProduct.isPending || mdUpdateProduct.isPending}
        >
          Зберегти
        </Button>
      </div>
    </>
  );
}
